/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import {ErrorType, ResponseData, showDialog, useEvent} from '@dvrd/dvr-controls';
import Invoice from "../../../models/invoiceModel";
import {onShowConfirmSnackbar} from "../../../util/renderUtil";
import {createErrorMessage, replaceDateVariables} from "../../../util/utils";
import AssignInvoiceNumber from "./assignInvoiceNumber";
import {ActivateHandle} from "../../../util/interfaces";
import {AppContext} from "../../misc/appContext";
import {getLatestInvoiceNumber} from "../../../util/requestSender";
import Account from "../../../models/accountModel";

interface Props {
    onUpdate: (invoice: Invoice) => void;
}

function AssignInvoiceNumberController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onUpdate} = props;
    const context = useContext(AppContext);
    const [invoice, setInvoice] = useState(new Invoice());
    const [prefix, setPrefix] = useState('');
    const [num, setNum] = useState('');
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorType>(null);
    const [latestNum, setLatestNum] = useState('0001');
    useEvent('testInvoiceNumber', onActivate);

    function onActivate(invoice: Invoice) {
        setInvoice(invoice ?? new Invoice());
        setActive(true);
        loadLatestNum();
    }

    function onClose() {
        setActive(false);
        setNum('');
        setPrefix('');
        setInvoice(new Invoice());
    }

    function onChangePrefix(value: string) {
        onResetError();
        setPrefix(value);
    }

    function onChangeNum(value: string) {
        onResetError();
        setNum(value);
    }

    function onResetError() {
        setError(null);
    }

    function onSubmit() {
        if (!validateValues()) return;
        setLoading(true);
        invoice.assignInvoiceNum(prefix, num, (invoice: Invoice, success: boolean, data: ResponseData) => {
            setLoading(false);
            const {message, error, account} = data;
            if (success) {
                onUpdate(invoice);
                onClose();
                onShowConfirmSnackbar('Het factuurnummer is ingesteld');
                context.onChangeContext({account: new Account(account)});
            } else if (error) setError(error);
            else showDialog(createErrorMessage(message ?? 'Het instellen van het factuurnummer is niet gelukt.'), 'Instellen mislukt');
        });
    }

    function validateValues(): boolean {
        if (!num.length) {
            setError('Het factuurnummer kan niet leeg zijn');
            return false;
        } else if (isNaN(Number(num))) {
            setError('Het factuurnummer moet een geldig getal zijn');
            return false;
        }
        return true;
    }

    function getAccountPrefix(): string {
        const prefix = context.accountContext.account?.settings?.invoicePrefix ?? '{jaar}-';
        return replaceDateVariables(prefix, invoice.invoiceDate);
    }

    function loadLatestNum() {
        const {account} = context.accountContext;
        if (!account) return;
        const prefix = getAccountPrefix();
        getLatestInvoiceNumber({
            data: {
                invoice_num_prefix: prefix, account_id: account.id,
            }, callback: (data: ResponseData) => {
                if (data.success && data.latest_num) setLatestNum(data.latest_num);
            }
        });
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <AssignInvoiceNumber onChangePrefix={onChangePrefix} onChangeNum={onChangeNum} onClose={onClose}
                             onSubmit={onSubmit} onResetError={onResetError} invoice={invoice} num={num} prefix={prefix}
                             active={active} loading={loading} error={error} accountPrefix={getAccountPrefix()}
                             latestNum={latestNum}/>
    )
}

export default forwardRef<ActivateHandle, Props>(AssignInvoiceNumberController);